// Real-time AI Signals Hook
import { useEffect, useState, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { aiSignalsAPI, AISignal, SignalFilters, SignalsResponse, ModelPerformance } from "@/lib/ai-signals-api";
import { useAISignalsWebSocket } from "./use-ai-signals-websocket";
import { logger } from "@/lib/logger";

export function useAISignals(
  filters: SignalFilters = {},
  page = 1,
  perPage = 20,
  sortBy = 'created_at',
  sortOrder: 'asc' | 'desc' = 'desc',
  enableRealtime = true
) {
  const queryClient = useQueryClient();
  const [isSubscribed, setIsSubscribed] = useState(false);

  // WebSocket integration
  const { isConnected, isConnecting, error: wsError, connectionStatus, latestSignal } = useAISignalsWebSocket();

  const queryKey = ["ai-signals", { filters, page, perPage, sortBy, sortOrder }];

  // Fetch signals with centralized backend + database merging and deduplication
  const { data, isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      return await logger.withTimer('fetch_ai_signals', async () => {
        logger.info('Fetching AI signals', {
          filters,
          page,
          perPage,
          sortBy,
          sortOrder,
          enableRealtime,
          wsConnected: isConnected,
        });
        return await aiSignalsAPI.getSignals(filters, page, perPage, sortBy, sortOrder);
      }, {
        filters: JSON.stringify(filters),
        page,
        perPage,
        sortBy,
        sortOrder,
      });
    },
    refetchInterval: enableRealtime && !isConnected ? 30000 : false, // Refetch every 30 seconds only if WebSocket disconnected
  });

  // Real-time subscription for signals (only when WebSocket disconnected)
  useEffect(() => {
    if (!enableRealtime || isConnected) return;

    const channel = supabase
      .channel("ai-signals-realtime")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "ai_signals",
        },
        (payload) => {
          console.log("AI Signal change received (fallback):", payload);

          // Invalidate and refetch signals query
          queryClient.invalidateQueries({ queryKey: ["ai-signals"] });

          // Also invalidate individual signal queries if we have an ID
          if (payload.new && typeof payload.new === 'object' && 'id' in payload.new) {
            queryClient.invalidateQueries({ queryKey: ["ai-signal", (payload.new as any).id] });
          }
        }
      )
      .subscribe((status) => {
        setIsSubscribed(status === "SUBSCRIBED");
      });

    return () => {
      supabase.removeChannel(channel);
      setIsSubscribed(false);
    };
  }, [enableRealtime, isConnected, queryClient]);

  // Log errors
  useEffect(() => {
    if (error) {
      logger.error('AI signals query failed', {
        filters: JSON.stringify(filters),
        page,
        perPage,
        sortBy,
        sortOrder,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }, [error, filters, page, perPage, sortBy, sortOrder]);

  return {
    signals: data?.signals || [],
    totalCount: data?.total_count || 0,
    page: data?.page || page,
    perPage: data?.per_page || perPage,
    hasNext: data?.has_next || false,
    hasPrev: data?.has_prev || false,
    isLoading,
    error,
    refetch,
    isSubscribed,
    wsConnected: isConnected,
    wsError,
    connectionStatus,
    isConnecting,
    latestSignal,
  };
}

export function useAISignal(signalId: string | undefined) {
  const queryClient = useQueryClient();

  const { data: signal, isLoading, error } = useQuery({
    queryKey: ["ai-signal", signalId],
    queryFn: async () => {
      if (!signalId) throw new Error("Signal ID required");
      return await aiSignalsAPI.getSignal(signalId);
    },
    enabled: !!signalId,
  });

  // Real-time subscription for specific signal
  useEffect(() => {
    if (!signalId) return;

    const channel = supabase
      .channel(`ai-signal-${signalId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "ai_signals",
          filter: `id=eq.${signalId}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["ai-signal", signalId] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [signalId, queryClient]);

  return { signal, isLoading, error };
}

export function useModelPerformance() {
  const { data: models, isLoading, error } = useQuery({
    queryKey: ["model-performance"],
    queryFn: async () => {
      try {
        // Try backend first
        const backendMetrics = await aiBackendClient.getPerformanceMetrics();

        // Map PerformanceMetrics to ModelPerformance[]
        const modelPerformance: ModelPerformance = {
          model_name: "AI Backend Model", // Default name since backend doesn't specify
          model_version: "1.0",
          total_signals: backendMetrics.total_signals,
          accuracy_rate: backendMetrics.success_rate,
          avg_confidence: backendMetrics.avg_confidence,
          profitable_signals: Math.floor(backendMetrics.total_signals * backendMetrics.success_rate),
          total_return: backendMetrics.total_return,
          max_drawdown: backendMetrics.max_drawdown,
          sharpe_ratio: backendMetrics.sharpe_ratio,
          win_rate: backendMetrics.win_rate,
          avg_holding_period: backendMetrics.avg_holding_period,
          last_updated: backendMetrics.last_updated,
        };

        return [modelPerformance];
      } catch (backendError) {
        console.warn("Backend performance metrics failed, falling back to Supabase:", backendError);
        // Fallback to Supabase
        return await aiSignalsAPI.getModelPerformance();
      }
    },
    refetchInterval: 60000, // Refetch every minute
  });

  return { models: models || [], isLoading, error };
}

export function useRiskMetrics() {
  const { data: riskMetrics, isLoading, error } = useQuery({
    queryKey: ["risk-metrics"],
    queryFn: () => aiSignalsAPI.getRiskMetrics(),
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  return { riskMetrics, isLoading, error };
}

export function usePortfolioOptimization() {
  const { data: optimization, isLoading, error } = useQuery({
    queryKey: ["portfolio-optimization"],
    queryFn: () => aiSignalsAPI.getPortfolioOptimization(),
    refetchInterval: 60000, // Refetch every minute
  });

  return { optimization, isLoading, error };
}

export function useSignalStats() {
  const { data: stats, isLoading, error } = useQuery({
    queryKey: ["signal-stats"],
    queryFn: async () => {
      try {
        // Try backend first
        const backendMetrics = await aiBackendClient.getPerformanceMetrics();

        // Map PerformanceMetrics to SignalStats interface
        const signalStats = {
          total_signals: backendMetrics.total_signals,
          active_signals: Math.floor(backendMetrics.total_signals * 0.3), // Estimate active signals
          executed_today: Math.floor(backendMetrics.total_signals * 0.1), // Estimate executed today
          success_rate: backendMetrics.success_rate,
          avg_confidence: backendMetrics.avg_confidence,
          top_performing_model: "AI Backend Model", // Default since backend doesn't specify
          sharpe_ratio: backendMetrics.sharpe_ratio,
          last_updated: backendMetrics.last_updated,
        };

        return signalStats;
      } catch (backendError) {
        console.warn("Backend signal stats failed, falling back to Supabase:", backendError);
        // Fallback to Supabase
        return await aiSignalsAPI.getSignalStats();
      }
    },
    refetchInterval: 15000, // Refetch every 15 seconds
  });

  return { stats, isLoading, error };
}

export function useSignalExecution() {
  const queryClient = useQueryClient();

  const executeSignal = useCallback(async (signalId: string, userWallet?: string) => {
    try {
      const result = await aiSignalsAPI.executeSignal(signalId, userWallet);

      if (result.success) {
        // Invalidate relevant queries
        queryClient.invalidateQueries({ queryKey: ["ai-signals"] });
        queryClient.invalidateQueries({ queryKey: ["ai-signal", signalId] });
        queryClient.invalidateQueries({ queryKey: ["signal-stats"] });
      }

      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to execute signal";

      return {
        success: false,
        error: errorMessage
      };
    }
  }, [queryClient]);

  const cancelSignal = useCallback(async (signalId: string) => {
    try {
      const result = await aiSignalsAPI.cancelSignal(signalId);

      if (result.success) {
        // Invalidate relevant queries
        queryClient.invalidateQueries({ queryKey: ["ai-signals"] });
        queryClient.invalidateQueries({ queryKey: ["ai-signal", signalId] });
        queryClient.invalidateQueries({ queryKey: ["signal-stats"] });
      }

      return result;
    } catch (error) {
      console.error("Error cancelling signal:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to cancel signal";

      return {
        success: false,
        error: errorMessage
      };
    }
  }, [queryClient]);

  return { executeSignal, cancelSignal };
}

export function useAvailableModels() {
  const { data: models, isLoading, error } = useQuery({
    queryKey: ["available-models"],
    queryFn: () => aiSignalsAPI.getAvailableModels(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return { models: models || [], isLoading, error };
}

// Hook for filtering and sorting signals
export function useSignalFilters() {
  const [filters, setFilters] = useState<SignalFilters>({});
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const updateFilter = useCallback((key: keyof SignalFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({});
  }, []);

  const setSort = useCallback((newSortBy: string, newSortOrder: 'asc' | 'desc') => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
  }, []);

  return {
    filters,
    sortBy,
    sortOrder,
    updateFilter,
    clearFilters,
    setSort,
  };
}

// Hook for signal history data - uses mock data since table doesn't exist
interface SignalHistoryItem {
  date: string;
  price: number;
  confidence: number;
  accuracy?: number;
  actual_return?: number;
}

export function useSignalHistory() {
  const { data: signalHistory, isLoading, error } = useQuery({
    queryKey: ["signal-history"],
    queryFn: async (): Promise<SignalHistoryItem[]> => {
      // Generate mock signal history data for display
      const mockData: SignalHistoryItem[] = [];
      const now = Date.now();
      for (let i = 50; i >= 0; i--) {
        mockData.push({
          date: new Date(now - i * 24 * 60 * 60 * 1000).toISOString(),
          price: 0.00001 + Math.random() * 0.00005,
          confidence: 0.5 + Math.random() * 0.5,
          accuracy: Math.random(),
          actual_return: (Math.random() - 0.5) * 0.2,
        });
      }
      return mockData;
    },
    refetchInterval: 60000,
  });

  return { signalHistory: signalHistory || [], isLoading, error };
}

// Hook for win rate by confidence level - uses mock data since table doesn't exist
export function useWinRateByConfidence() {
  const { data: winRateData, isLoading, error } = useQuery({
    queryKey: ["win-rate-by-confidence"],
    queryFn: async () => {
      // Return mock data since signal_history table doesn't exist
      return [
        { level: 'HIGH', winRate: 75, total: 120, wins: 90 },
        { level: 'MEDIUM', winRate: 62, total: 200, wins: 124 },
        { level: 'LOW', winRate: 48, total: 80, wins: 38 },
      ];
    },
    refetchInterval: 60000,
  });

  return { winRateData: winRateData || [], isLoading, error };
}

// Hook for cumulative returns over time - uses mock data since table doesn't exist
export function useCumulativeReturns() {
  const { data: cumulativeReturns, isLoading, error } = useQuery({
    queryKey: ["cumulative-returns"],
    queryFn: async () => {
      // Generate mock cumulative returns data
      const returns = [];
      let cumulative = 0;
      const now = Date.now();
      for (let i = 30; i >= 0; i--) {
        cumulative += (Math.random() - 0.4) * 0.05; // Slight upward bias
        returns.push({
          date: new Date(now - i * 24 * 60 * 60 * 1000).toISOString(),
          cumulativeReturn: cumulative,
          index: 30 - i
        });
      }
      return returns;
    },
    refetchInterval: 60000,
  });

  return { cumulativeReturns: cumulativeReturns || [], isLoading, error };
}